/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps
} from '@msdyn365-commerce-modules/buybox';
import { ArrayExtensions, DeliveryMode } from '@msdyn365-commerce-modules/retail-actions';
import { IBuyboxViewProps } from './hei-buybox-v2';
import { IBuyboxFindInStoreViewProps } from './components/hei-buybox-v2-find-in-store';
import { AuthorizedDealerBadge } from './components/AuthorizedDealerBadge';
import { ProductCode } from './components/ProductCode';
import { ProductBadge } from './components/ProductBadge';
import Bazzarvoice from '../bazzarvoice-widget/components/Bazzarvoice';
import { widgetType } from '../bazzarvoice-widget/bazzarvoice-widget.props.autogenerated';
import { Promotions } from './components/Promotions';
import { WarrantySection } from './components/WarrantySection';
import useIsInViewport from 'use-is-in-viewport';
import { ShopUsed } from './components/ShopUsed';
import { FloatingSummary } from './components/FloatingSummary';
import { MediaGalleryOverlay } from './components/MediaGalleryOverlay';
import { JsonLinkingData } from './components/JsonLinkingData';
import { KlaviyoBackInStockSubscription } from './components/KlaviyoBackInStockSubscription';
import { GaPayload } from '../../common/pushGoogleAnalyticsEvent';
import { useGoogleAnalytics } from '../../common/useGoogleAnalytics';
import { KlaviyoPriceDropSubscription } from './components/KlaviyoPriceDropSubscription';

const BuyboxView: React.FC<IBuyboxViewProps> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        // addToOrderTemplate,
        addToWishlist,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        inventoryLabel,
        // shopSimilarLook,
        // keyInPrice,
        // shopSimilarDescription,
        // unitOfMeasure,
        // productComparisonButton,
        context,
        data: { product, attributeValues, categoryPaths },
        config: { helpLogo },
        state,
        callbacks,
        resources
    } = props;
    const { request } = context;
    const { productPromotions, applicableWarranties, productPrice, productVariantData } = state;
    const promotions = React.useMemo(() => {
        const promotions = productPromotions?.Promotions ?? [];
        if (promotions.length > 0) {
            promotions[0].IsImportant = true;
        }

        return promotions;
    }, [productPromotions?.Promotions]);
    const [isInViewport, targetRef] = useIsInViewport();
    // process.env.NODE_ENV === 'development' && console.log('BuyboxView', 'isInViewport', isInViewport);

    const gaPayload = React.useMemo(() => {
        if (productVariantData && productPrice && productVariantData.RecordId === productPrice.ProductId) {
            const hasUnselectedOption = productVariantData.Dimensions?.find(d => Boolean(!d.DimensionValue));

            if (hasUnselectedOption) {
                return undefined;
            }

            const brand = productVariantData?.ExtensionProperties?.find(ep => ep.Key === 'Brand')?.Value?.StringValue;

            const paths = categoryPaths?.result?.map(p => p.Name) ?? [];
            const variant = productVariantData.Dimensions?.map(d => d.DimensionValue?.Value).join(',');

            const discount = productPrice.DiscountAmount ?? 0;
            let price = (productPrice.BasePrice ?? 0) - discount;
            if (productPrice.CustomerContextualPrice) {
                price = productPrice.CustomerContextualPrice;
            }

            const pricePlayload = Math.round(price * 100) / 100;
            const discountPlayload = Math.round(discount * 100) / 100;

            process.env.NODE_ENV === 'development' && console.log('BuyboxView', pricePlayload, discountPlayload, variant);

            const payload: GaPayload = {
                event: 'view_item',
                ecommerce: {
                    currency: productPrice.CurrencyCode,
                    value: productPrice.CustomerContextualPrice ?? 0,
                    items: [
                        {
                            item_id: productVariantData.ProductNumber ?? productVariantData.ItemId ?? '',
                            item_name: productVariantData.Name ?? '',
                            item_brand: brand ?? undefined,
                            item_variant: variant,
                            item_category: paths[0],
                            item_category2: paths[1],
                            item_category3: paths[2],
                            item_category4: paths[3],
                            item_category5: paths[4],
                            price: pricePlayload,
                            discount: discountPlayload,
                            quantity: 1,
                            pid: productVariantData.ItemId ?? '',
                            index: 0
                        }
                    ]
                }
            };

            return payload;
        }

        return undefined;
    }, [productVariantData, productPrice, categoryPaths]);

    useGoogleAnalytics(gaPayload);

    return (
        <Module {...ModuleProps} ref={targetRef}>
            <div className='buybox-mobile-container'>
                <Node {...ProductInfoContainerProps}>
                    <div className='buybox-title-container'>
                        {title} <AuthorizedDealerBadge product={product.result} requestContext={request} />
                    </div>
                    <div className='buybox-product-code'>
                        <ProductCode product={product.result} resources={resources} />
                    </div>
                </Node>
            </div>
            <div className='buybox-top-container'>
                <div className='media-gallery-container'>
                    <Node {...MediaGalleryContainerProps}>
                        <ProductBadge attributeValues={attributeValues.result} requestContext={request} productPrice={productPrice} />
                        {props.mediaGallery}
                        <MediaGalleryOverlay product={product.result} resources={resources} />
                    </Node>
                    <div className='hei-buybox-desc'>
                        {description && <div className='hei-buybox-desc-title'>About this product</div>}
                        <div className='hei-buybox-desc-content'>
                            {description}
                            <a className='hei-buybox-desc-link' href='#overview'>
                                Find out more
                            </a>
                        </div>
                    </div>
                </div>
                <Node {...ProductInfoContainerProps}>
                    <div className='buybox-title-container'>
                        {title} <AuthorizedDealerBadge product={product.result} requestContext={request} />
                    </div>
                    <div className='buybox-product-code'>
                        <ProductCode product={product.result} resources={resources} />
                    </div>
                    <div className='buybox-ratings-price-container'>
                        <Bazzarvoice
                            itemId={product.result?.ItemId}
                            widgetType={widgetType.rating_summary}
                            className='hei-bazzarvoice-rating'
                        />
                        <div>
                            {price}
                            <KlaviyoPriceDropSubscription
                                context={context.actionContext}
                                product={state.productVariantData}
                                productPrice={productPrice}
                            />
                        </div>
                    </div>
                    <Promotions promotions={promotions} sitePath={request.sitePath} />
                    {/* {unitOfMeasure} */}
                    {/* {props.bulkPurchaseLink} */}
                    <div className='buybox-mobile-container'>
                        <Node {...ProductInfoContainerProps}>
                            <div className='hei-buybox-desc'>
                                <div className='hei-buybox-desc-title'>About this product</div>
                                <div className='hei-buybox-desc-content'>
                                    {description}
                                    <a className='hei-buybox-desc-link' href='#overview'>
                                        Find out more
                                    </a>
                                </div>
                            </div>
                        </Node>
                    </div>
                    <WarrantySection
                        warrantyGroups={applicableWarranties}
                        coreContext={context}
                        selectedWarranty={state.selectedWarranty}
                        setSelectedWarranty={w => callbacks.setSelectedWarranty(w)}
                        logo={helpLogo}
                    />
                    {/* {keyInPrice && _renderKeyInPrice(keyInPrice)} */}
                    <div className='hei-buybox-add-to-cart'>
                        <div className='ms-buybox__inventory-container'>
                            {inventoryLabel}
                            {_renderAvailability(props) || (findInStore && _renderFindInStore(findInStore))}
                        </div>
                        <div className='ms-buybox__actions-container'>
                            {quantity && _renderQuantity(quantity)}
                            <KlaviyoBackInStockSubscription
                                context={context.actionContext}
                                product={state.productVariantData}
                                productAvailableQuantity={state.productAvailableQuantity}
                            />
                            {addToCart && _renderAddToCart(addToCart)}
                            {addToWishlist && _renderAddToWishlist(addToWishlist)}
                            {/* {productComparisonButton} */}
                        </div>
                    </div>
                    {/* {addToOrderTemplate && _renderAddToOrderTemplate(addToOrderTemplate)} */}
                    {/* {_renderSocialShare(props.slots && props.slots.socialShare)} */}
                    {configure && _renderConfigure(configure)}
                    {/* {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)} */}
                    {/* {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)} */}
                    <ShopUsed product={product.result} resources={resources} context={context.actionContext} />
                    {ArrayExtensions.hasElements(props.slots.actionBlocks) && props.slots.actionBlocks.map(b => b)}
                </Node>
                <FloatingSummary {...props} isInViewport={isInViewport ?? false} />
            </div>
            <JsonLinkingData
                productInfo={productVariantData ?? product.result}
                productPrice={productPrice}
                actionContext={context.actionContext}
            />
        </Module>
    );
};

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

// @ts-ignore
const _renderAddToOrderTemplate = (addToOrderTemplate: IBuyboxAddToOrderTemplateViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToOrderTemplate;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToWishlist;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

// @ts-ignore
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            {input}
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
        </Node>
    );
};

// @ts-ignore
const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderAvailability = (props: IBuyboxViewProps) => {
    let productAvailableQuantity = props.state.productAvailableQuantity;

    if (!productAvailableQuantity && ArrayExtensions.hasElements(props.data.productAvailableQuantity.result)) {
        productAvailableQuantity = props.data.productAvailableQuantity.result.find(
            shipping => shipping.deliveryType === DeliveryMode.shipping
        );
    }
    const extensionProperties = (props.state.productVariantData ?? props.data.product.result)?.ExtensionProperties;

    const availDate = extensionProperties?.find(ep => ep.Key === 'AvailDate')?.Value?.StringValue;
    const isBopisOnly = Boolean(extensionProperties?.find(ep => ep.Key === 'Bopis' && ep.Value?.StringValue === 'Yes'));

    process.env.NODE_ENV === 'development' && console.log('_renderAvailability', availDate, isBopisOnly);

    if (isBopisOnly || !availDate || !productAvailableQuantity) {
        return undefined;
    }

    if (productAvailableQuantity) {
        process.env.NODE_ENV === 'development' && console.log('_renderAvailability', productAvailableQuantity.StockLevelCode);
        if (productAvailableQuantity.StockLevelCode === 'AVAIL') {
            return (
                <p className='ms-buybox__expected-avail'>
                    {props.resources.expectedAvailabilityLabel}:
                    <br />
                    {availDate}
                </p>
            );
        }
    }

    return undefined;
};

// @ts-ignore
const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};
export default BuyboxView;
